/**
 * Read-only symptom and domain decision tables for the Semi-Structured Interview.
 *
 * Used in the SSI Review completion dialog (SLaM / 3 Borough) so the clinician
 * can confirm present / not-present decisions before generating the report.
 * Icons match the in-page summary dialogs on 3_SSIReview.js.
 */

import React from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/solid';

/** Present / not present / unanswered icon for a boolean decision cell. */
const DecisionIcon = ({ value }) => {
  if (value === true) {
    return <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" aria-label="Present" />;
  }
  if (value === false) {
    return <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" aria-label="Not present" />;
  }
  // Only the CYP SSI records this third answer; adult values never reach it.
  if (value === 'inconclusive') {
    return <span className="text-amber-800 text-xs font-semibold">Inconclusive</span>;
  }
  return <span className="text-gray-400 text-xs">{'\u2014'}</span>;
};

/**
 * @param {Object} props
 * @param {Array<{criterion: string, symptom: string, adulthood: boolean|null, childhood: boolean|null}>} props.attentionDeficitData
 * @param {Array} props.hyperactivityImpulsivityData
 * @param {{ adulthoodTotal: number, childhoodTotal: number }} props.attentionTotals
 * @param {{ adulthoodTotal: number, childhoodTotal: number }} props.hyperactivityTotals
 * @param {Array<{domain: string, adulthood: boolean|null, childhood: boolean|null}>} props.domainImpairmentData
 * @param {boolean} [props.compact=false] - Slightly tighter cells for dialog use
 * @param {Object} [props.labels] - Column titles; adult defaults. CYP passes
 *   its own ("Present in the last 6 months" / "Present before age 12").
 * @param {boolean} [props.mergeColumns=false] - Render ONE decision column
 *   (the `recent` values) under `labels.merged*`. Used for CYP under-13s,
 *   where the onset question is not asked and the two columns would repeat
 *   the same determination.
 * @param {boolean} [props.mergeDomainColumns=mergeColumns] - Same merge for
 *   the domains table only. CYP passes true at EVERY age: childhood domain
 *   impairment is never assessed on the CYP SSI, so its column would show a
 *   question nobody was asked.
 */
const SSIDecisionSummaryTables = ({
  attentionDeficitData = [],
  hyperactivityImpulsivityData = [],
  attentionTotals = { adulthoodTotal: 0, childhoodTotal: 0 },
  hyperactivityTotals = { adulthoodTotal: 0, childhoodTotal: 0 },
  domainImpairmentData = [],
  compact = false,
  labels = {},
  mergeColumns = false,
  mergeDomainColumns = mergeColumns,
}) => {
  const cellPad = compact ? 'p-2' : 'p-3';
  const symptomText = compact ? 'text-xs' : 'text-xs';
  const {
    recent = 'Adulthood',
    earlier = 'Childhood',
    merged = 'Adulthood',
    domainRecent = recent,
    domainEarlier = earlier,
    domainMerged = merged,
  } = labels;

  return (
    <div className="space-y-6 text-left">
      <div>
        <h4 className="text-sm font-semibold text-gray-800 mb-2">Symptoms</h4>
        <table className="w-full text-left shadow-md rounded-lg overflow-hidden" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '10%' }} />
            <col style={{ width: '50%' }} />
            <col style={{ width: mergeColumns ? '40%' : '20%' }} />
            {!mergeColumns && <col style={{ width: '20%' }} />}
          </colgroup>
          <thead>
            <tr className="bg-primary text-white">
              <th className={`${cellPad} text-center text-xs`}>Criterion</th>
              <th className={`${cellPad} text-xs`}>Symptom</th>
              <th className={`${cellPad} text-center text-xs`}>{mergeColumns ? merged : recent}</th>
              {!mergeColumns && <th className={`${cellPad} text-center text-xs`}>{earlier}</th>}
            </tr>
          </thead>
          <tbody>
            {attentionDeficitData.map((item, index) => (
              <tr key={`ia-${index}`} className="bg-primary/5 border-b border-primary/10">
                <td className={`${cellPad} text-sm text-center font-bold text-primary`}>{item.criterion}</td>
                <td className={`${cellPad} ${symptomText}`}>{item.symptom}</td>
                <td className={`${cellPad} text-center bg-white`}><DecisionIcon value={item.adulthood} /></td>
                {!mergeColumns && (
                  <td className={`${cellPad} text-center bg-white`}><DecisionIcon value={item.childhood} /></td>
                )}
              </tr>
            ))}
            <tr className="bg-primary/15 font-bold text-primary text-sm">
              <td className={cellPad} colSpan="2">Total — Inattention</td>
              <td className={`${cellPad} text-center`}>{attentionTotals.adulthoodTotal} / 9</td>
              {!mergeColumns && (
                <td className={`${cellPad} text-center`}>{attentionTotals.childhoodTotal} / 9</td>
              )}
            </tr>
            {hyperactivityImpulsivityData.map((item, index) => (
              <tr key={`hi-${index}`} className="bg-primary/5 border-b border-primary/10">
                <td className={`${cellPad} text-sm text-center font-bold text-primary`}>{item.criterion}</td>
                <td className={`${cellPad} ${symptomText}`}>{item.symptom}</td>
                <td className={`${cellPad} text-center bg-white`}><DecisionIcon value={item.adulthood} /></td>
                {!mergeColumns && (
                  <td className={`${cellPad} text-center bg-white`}><DecisionIcon value={item.childhood} /></td>
                )}
              </tr>
            ))}
            <tr className="bg-primary/15 font-bold text-primary text-sm">
              <td className={cellPad} colSpan="2">Total — Hyperactivity / Impulsivity</td>
              <td className={`${cellPad} text-center`}>{hyperactivityTotals.adulthoodTotal} / 9</td>
              {!mergeColumns && (
                <td className={`${cellPad} text-center`}>{hyperactivityTotals.childhoodTotal} / 9</td>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {domainImpairmentData.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-800 mb-2">Day-to-day impact (domains)</h4>
          <table className="w-full text-left shadow-md rounded-lg overflow-hidden" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '60%' }} />
              <col style={{ width: mergeDomainColumns ? '40%' : '20%' }} />
              {!mergeDomainColumns && <col style={{ width: '20%' }} />}
            </colgroup>
            <thead>
              <tr className="bg-primary text-white">
                <th className={`${cellPad} text-xs`}>Domain</th>
                <th className={`${cellPad} text-center text-xs`}>{mergeDomainColumns ? domainMerged : domainRecent}</th>
                {!mergeDomainColumns && <th className={`${cellPad} text-center text-xs`}>{domainEarlier}</th>}
              </tr>
            </thead>
            <tbody>
              {domainImpairmentData.map((item, index) => (
                <tr key={`dom-${index}`} className="bg-primary/5 border-b border-primary/10">
                  <td className={`${cellPad} text-sm font-semibold text-primary`}>{item.domain}</td>
                  <td className={`${cellPad} text-center bg-white`}><DecisionIcon value={item.adulthood} /></td>
                  {!mergeDomainColumns && (
                    <td className={`${cellPad} text-center bg-white`}><DecisionIcon value={item.childhood} /></td>
                  )}
                </tr>
              ))}
              <tr className="bg-primary/15 font-bold text-primary text-sm">
                <td className={cellPad}>Total domains marked present</td>
                <td className={`${cellPad} text-center`}>
                  {domainImpairmentData.filter((d) => d.adulthood).length} / {domainImpairmentData.length}
                </td>
                {!mergeDomainColumns && (
                  <td className={`${cellPad} text-center`}>
                    {domainImpairmentData.filter((d) => d.childhood).length} / {domainImpairmentData.length}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SSIDecisionSummaryTables;
